import { createCareerCertificationApi, updateCareerCertificationApi } from "@/api/services/global/master";
import { TGetCareerCertificationListOutputDto } from "@/api/services/global/master/dtos/get-master-career-certification-list.dto";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Modal } from "@/components/elements/Modal";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { masterDataCareerCertificationSchema } from "@/features/master-data/schema/masterDataCareerCertificationSchema";
import { TMasterCareerCertificationForm } from "@/features/master-data/types/masterDataCareerCertificationForm";
import { setErrorForm, showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useMemo } from "react";
import { useForm } from "react-hook-form";

type TCareerCertificationFormModalProps = {
  onClose: () => void;
  isOpen: boolean;
  selectedCareerCertification?: TGetCareerCertificationListOutputDto;
};

export const CareerCertificationFormModal: FC<TCareerCertificationFormModalProps> = ({ onClose, isOpen, selectedCareerCertification }) => {
  const queryClient = useQueryClient();

  const defaultValues = useMemo(
    () => ({
      certificationName: selectedCareerCertification ? selectedCareerCertification.certificationName : "",
      careerCertificationRenewVisaCsvCode: selectedCareerCertification ? selectedCareerCertification.careerCertificationRenewVisaCsvCode : "",
    }),
    [selectedCareerCertification],
  );

  const methods = useForm<TMasterCareerCertificationForm>({
    resolver: yupResolver(masterDataCareerCertificationSchema),
    mode: "all",
    reValidateMode: "onBlur",
    values: defaultValues,
  });

  const { control } = methods;

  const onSubmit = async (data: TMasterCareerCertificationForm) => {
    try {
      if (selectedCareerCertification) {
        const rs = await updateCareerCertificationApi({
          careerCertifications: [
            {
              id: toNumber(selectedCareerCertification.id),
              certificationName: data.certificationName,
              careerCertificationRenewVisaCsvCode: data.careerCertificationRenewVisaCsvCode,
            },
          ],
        });
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      } else {
        const rs = await createCareerCertificationApi({
          certificationName: data.certificationName,
          careerCertificationRenewVisaCsvCode: data.careerCertificationRenewVisaCsvCode,
        });
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      }
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GLOBAL.MASTER_CAREER_CERTIFICATION] });
      onClose();
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Modal title={selectedCareerCertification ? "編集" : "追加"} isOpen={isOpen} enableFooter={false} onClose={onClose} enableCloseIcon={false}>
      <Form methods={methods} onSubmit={onSubmit} schema={masterDataCareerCertificationSchema}>
        <Stack gap={3} my={3}>
          <Grid item xs={6}>
            <FormField control={control} name="certificationName">
              <Input />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="careerCertificationRenewVisaCsvCode">
              <Input />
            </FormField>
          </Grid>
        </Stack>
        <Stack flexDirection="row" justifyContent="center" gap="24px">
          <Button variant="outline" onClick={onClose}>
            キャンセル
          </Button>
          <Button type="submit">保存</Button>
        </Stack>
      </Form>
    </Modal>
  );
};
